import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useAuthStore } from '../store/useAuthStore';
import { Eye, EyeOff, MessageSquare, ArrowRight, Send } from 'lucide-react';
import { motion } from 'motion/react';
import { getTelegramInitData, isTelegramMiniApp } from '../lib/telegram';

export const LoginScreen = () => {
  const navigate = useNavigate();
  const { login, loginWithTelegram, isLoading, loginError, isAuthenticated, isBootstrapping } = useAuthStore();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [shakeError, setShakeError] = useState(false);
  const usernameRef = useRef<HTMLInputElement>(null);
  const miniAppMode = isTelegramMiniApp();

  useEffect(() => {
    if (isAuthenticated) navigate('/', { replace: true });
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!miniAppMode) {
      usernameRef.current?.focus();
    }
  }, [miniAppMode]);

  useEffect(() => {
    if (!miniAppMode || isBootstrapping || isAuthenticated) {
      return;
    }

    void loginWithTelegram(getTelegramInitData());
  }, [isAuthenticated, isBootstrapping, loginWithTelegram, miniAppMode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password) return;
    const ok = await login(username, password);
    if (ok) {
      navigate('/', { replace: true });
    } else {
      setShakeError(true);
      setTimeout(() => setShakeError(false), 600);
    }
  };

  const handleTelegramLogin = async () => {
    const ok = await loginWithTelegram(getTelegramInitData());
    if (ok) {
      navigate('/', { replace: true });
    } else {
      setShakeError(true);
      setTimeout(() => setShakeError(false), 600);
    }
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#F2F2F7] via-[#E5E5F0] to-[#D8D8E8] flex items-center justify-center px-5 py-10">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-[#007AFF]/8 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-[#5856D6]/8 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', damping: 26, stiffness: 300 }}
        className="w-full max-w-[360px] relative"
      >
        {/* Card */}
        <div className="bg-white/90 backdrop-blur-2xl rounded-[28px] shadow-[0_20px_60px_rgba(0,0,0,0.15),0_4px_20px_rgba(0,0,0,0.08)] overflow-hidden border border-white/60">
          
          {/* Header section */}
          <div className="px-8 pt-10 pb-8 flex flex-col items-center bg-gradient-to-b from-[#007AFF]/5 to-transparent">
            <div className="w-[72px] h-[72px] rounded-[22px] bg-gradient-to-br from-[#007AFF] to-[#5856D6] flex items-center justify-center shadow-[0_8px_24px_rgba(0,122,255,0.35)] mb-5">
              <MessageSquare size={34} className="text-white" strokeWidth={1.8} />
            </div>
            <h1 className="text-[24px] font-bold text-black tracking-tight leading-tight text-center">
              Support Panel
            </h1>
            <p className="text-[14px] text-[#8E8E93] mt-1.5 text-center">
              Панель оператора службы поддержки
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 pb-8 space-y-3">

            {/* Error message */}
            <motion.div
              animate={shakeError ? { x: [-8, 8, -6, 6, -3, 3, 0] } : { x: 0 }}
              transition={{ duration: 0.4 }}
              className="min-h-[20px]"
            >
              {loginError && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-[#FF3B30]/8 border border-[#FF3B30]/20 rounded-[12px] px-4 py-2.5 text-[14px] text-[#FF3B30] text-center"
                >
                  {loginError}
                </motion.div>
              )}
            </motion.div>

            {miniAppMode && (
              <button
                type="button"
                onClick={() => void handleTelegramLogin()}
                disabled={isLoading}
                className={`w-full h-[54px] rounded-[16px] text-[16px] font-semibold flex items-center justify-center gap-2.5 transition-all duration-200 ${
                  isLoading
                    ? 'bg-[#229ED9]/40 text-white/70 cursor-not-allowed'
                    : 'bg-[#229ED9] text-white shadow-[0_4px_16px_rgba(34,158,217,0.35)] hover:bg-[#168dc5] active:scale-[0.98]'
                }`}
              >
                <Send size={18} strokeWidth={2.3} />
                <span>Войти через Telegram</span>
              </button>
            )}

            {miniAppMode && (
              <div className="flex items-center gap-3 py-1">
                <div className="h-px flex-1 bg-black/8" />
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#8E8E93]">или</span>
                <div className="h-px flex-1 bg-black/8" />
              </div>
            )}

            {/* Username */}
            <div className="space-y-1.5">
              <label className="text-[13px] font-medium text-[#3C3C43]/70 pl-1">Логин</label>
              <div className="relative">
                <input
                  ref={usernameRef}
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="Введите логин"
                  autoComplete="username"
                  className="w-full h-[52px] bg-[#F2F2F7] rounded-[14px] px-4 text-[16px] text-black placeholder:text-[#C7C7CC] outline-none border-2 border-transparent focus:border-[#007AFF]/40 focus:bg-white transition-all"
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <label className="text-[13px] font-medium text-[#3C3C43]/70 pl-1">Пароль</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Введите пароль"
                  autoComplete="current-password"
                  className="w-full h-[52px] bg-[#F2F2F7] rounded-[14px] px-4 pr-12 text-[16px] text-black placeholder:text-[#C7C7CC] outline-none border-2 border-transparent focus:border-[#007AFF]/40 focus:bg-white transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8E8E93] hover:text-black transition-colors"
                >
                  {showPassword ? <EyeOff size={18} strokeWidth={2} /> : <Eye size={18} strokeWidth={2} />}
                </button>
              </div>
            </div>

            {/* Submit button */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={isLoading || !username.trim() || !password}
                className={`w-full h-[54px] rounded-[16px] text-[16px] font-semibold flex items-center justify-center gap-2.5 transition-all duration-200 ${
                  isLoading || !username.trim() || !password
                    ? 'bg-[#007AFF]/40 text-white/70 cursor-not-allowed'
                    : 'bg-[#007AFF] text-white shadow-[0_4px_16px_rgba(0,122,255,0.4)] hover:bg-[#0066DD] active:scale-[0.98]'
                }`}
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner />
                    <span>Входим...</span>
                  </>
                ) : (
                  <>
                    <span>Войти</span>
                    <ArrowRight size={18} strokeWidth={2.5} />
                  </>
                )}
              </button>
            </div>

          </form>
        </div>

        {/* Version */}
      </motion.div>
    </div>
  );
};

const LoadingSpinner = () => (
  <svg className="animate-spin w-5 h-5 text-white" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);
